import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { Role } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceCollaboratorsQueryKey } from '@/utils/queryKeys';
import type {
  UpdateExperienceCollaboratorMutation,
  UpdateExperienceCollaboratorMutationVariables,
} from './useUpdateCollaborator.generated';

const query = gql`
  mutation UpdateExperienceCollaborator(
    $input: UpdateExperiencePermissionInput!
  ) {
    updateExperiencePermission(input: $input)
  }
`;

export const useUpdateCollaborator = () => {
  const queryClient = useQueryClient();
  const { experience } = usePageExperienceContext();
  const experienceId = String(experience?.id);

  const { mutate, error, isPending, reset } = useMutation<
    UpdateExperienceCollaboratorMutation,
    QueryError,
    UpdateExperienceCollaboratorMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        UpdateExperienceCollaboratorMutation,
        UpdateExperienceCollaboratorMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createExperienceCollaboratorsQueryKey(experienceId),
      });
    },
  });

  const updateCollaborator = (
    userId: string,
    role: Role,
    options?: MutateOptions<
      UpdateExperienceCollaboratorMutation,
      QueryError,
      UpdateExperienceCollaboratorMutationVariables
    >
  ) => mutate({ input: { experienceId, userId, role } }, options);

  return {
    updateCollaborator,
    error,
    isPending,
    reset,
  };
};
